import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import en from "./locales/en";
import ar from "./locales/ar";
import ku from "./locales/ku";

export type Locale = "en" | "ar" | "ku";

type Messages = typeof en;
type WidenStrings<T> = T extends string
  ? string
  : T extends object
    ? { readonly [K in keyof T]: WidenStrings<T[K]> }
    : T;
type LocaleMessages = WidenStrings<Messages>;
type DotPrefix<T extends string> = T extends "" ? "" : `.${T}`;
type DotNestedKeys<T> = (
  T extends object
    ? {
        [K in Extract<keyof T, string>]: `${K}${DotPrefix<DotNestedKeys<T[K]>>}`;
      }[Extract<keyof T, string>]
    : ""
) extends infer D
  ? Extract<D, string>
  : never;

export type TranslationKey = DotNestedKeys<Messages>;

export const LANGUAGE_STORAGE_KEY = "oelox.partner.locale";

export const localeMeta: Record<
  Locale,
  { nativeName: string; englishName: string; dir: "ltr" | "rtl"; short: string }
> = {
  ar: { nativeName: "العربية", englishName: "Arabic", dir: "rtl", short: "AR" },
  ku: { nativeName: "کوردی", englishName: "Kurdish Sorani", dir: "rtl", short: "KU" },
  en: { nativeName: "English", englishName: "English", dir: "ltr", short: "EN" },
};

/** Display / selector order — Arabic first (default). */
export const LOCALE_ORDER: Locale[] = ["ar", "ku", "en"];

const dictionaries: Record<Locale, LocaleMessages> = {
  en,
  ar,
  ku,
};

interface I18nContextValue {
  locale: Locale;
  direction: "ltr" | "rtl";
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function readInitialLocale(): Locale {
  if (typeof window === "undefined") return "ar";
  const urlLang = new URLSearchParams(window.location.search).get("lang");
  if (isLocale(urlLang)) return urlLang;
  const saved = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return isLocale(saved) ? saved : "ar";
}

function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && value in dictionaries;
}

function getNestedValue(source: LocaleMessages, key: TranslationKey): string {
  const value = key.split(".").reduce<unknown>((current, segment) => {
    if (current && typeof current === "object" && segment in current) {
      return (current as Record<string, unknown>)[segment];
    }
    return undefined;
  }, source);

  return typeof value === "string" ? value : key;
}

function applyVars(
  template: string,
  vars?: Record<string, string | number>
): string {
  if (!vars) return template;
  return Object.entries(vars).reduce(
    (out, [k, v]) => out.replaceAll(`{${k}}`, String(v)),
    template
  );
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readInitialLocale);
  const direction = localeMeta[locale].dir;

  const setLocale = (nextLocale: Locale) => {
    setLocaleState(nextLocale);
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLocale);
  };

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = direction;
    document.documentElement.dataset.locale = locale;
    document.body.dir = direction;
  }, [locale, direction]);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      direction,
      setLocale,
      t: (key, vars) => {
        const local = getNestedValue(dictionaries[locale], key);
        const resolved = local !== key ? local : getNestedValue(dictionaries.en, key);
        return applyVars(resolved, vars);
      },
    }),
    [locale, direction]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used inside I18nProvider");
  return context;
}
